import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';
import { Loader2, User, Mail, Shield, Key, Save, Eye, EyeOff } from 'lucide-react';

export const AdminProfile = () => {
  const { token, admin: currentAdmin, refreshProfile } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Profile data
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState('');
  
  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile);
        setName(data.profile.name || '');
      } else {
        toast.error('Fehler beim Laden des Profils');
      }
    } catch (error) {
      console.error('Profile error:', error);
      toast.error('Fehler beim Laden des Profils');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [token, backendUrl]);

  const handleSaveProfile = async () => {
    if (!name.trim()) {
      toast.error('Name darf nicht leer sein');
      return;
    }
    if (name.trim().length < 2) {
      toast.error('Name muss mindestens 2 Zeichen haben');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: name.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile);
        toast.success('Profil gespeichert');
        
        // Refresh the admin context if available
        if (refreshProfile) {
          await refreshProfile();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword) {
      toast.error('Aktuelles Passwort erforderlich');
      return;
    }
    if (!newPassword) {
      toast.error('Neues Passwort erforderlich');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Neues Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    if (currentPassword === newPassword) {
      toast.error('Neues Passwort muss sich vom aktuellen unterscheiden');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/profile/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      if (response.ok) {
        toast.success('Passwort erfolgreich geändert');
        // Reset password fields
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowCurrentPassword(false);
        setShowNewPassword(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Ändern des Passworts');
      }
    } catch (error) {
      console.error('Password change error:', error);
      toast.error('Fehler beim Ändern des Passworts');
    } finally {
      setChangingPassword(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      'super_admin': 'Super Admin',
      'admin': 'Administrator',
      'support': 'Support',
      'viewer': 'Nur Lesen'
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      'super_admin': 'bg-purple-500/20 text-purple-400',
      'admin': 'bg-blue-500/20 text-blue-400',
      'support': 'bg-green-500/20 text-green-400',
      'viewer': 'bg-slate-500/20 text-slate-400'
    };
    return colors[role] || colors['viewer'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="admin-profile-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="admin-profile">
      <div>
        <h1 className="text-3xl font-bold text-white">Profil bearbeiten</h1>
        <p className="text-slate-400 mt-1">Verwalte deine persönlichen Einstellungen</p>
      </div>

      {/* Profile Info Card */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <User className="h-5 w-5 text-blue-400" />
            Persönliche Daten
          </CardTitle>
          <CardDescription className="text-slate-400">
            Grundlegende Informationen zu deinem Konto
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email (read-only) */}
          <div className="space-y-2">
            <Label className="text-slate-300 flex items-center gap-2">
              <Mail className="h-4 w-4" />
              E-Mail-Adresse
            </Label>
            <Input
              value={profile?.email || ''}
              disabled
              className="bg-slate-700/50 border-slate-600 text-slate-400 cursor-not-allowed"
            />
            <p className="text-xs text-slate-500">E-Mail-Adresse kann nicht geändert werden</p>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label className="text-slate-300">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dein Name"
              className="bg-slate-700 border-slate-600 text-white focus:border-blue-500"
              data-testid="profile-name-input"
            />
          </div>

          {/* Role (read-only) */}
          <div className="space-y-2">
            <Label className="text-slate-300 flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Rolle
            </Label>
            <div className="flex items-center gap-2">
              <Badge className={getRoleColor(profile?.role)}>
                {getRoleLabel(profile?.role)}
              </Badge>
              <span className="text-xs text-slate-500">
                Rolle kann nur von einem Super Admin geändert werden
              </span>
            </div>
          </div>

          {/* Last Login */}
          <div className="space-y-2">
            <Label className="text-slate-300">Letzter Login</Label>
            <p className="text-slate-400 text-sm">
              {profile?.last_login 
                ? new Date(profile.last_login).toLocaleString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : 'Noch nie'
              }
            </p>
          </div>

          <Button 
            onClick={handleSaveProfile} 
            disabled={saving || name === profile?.name}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            data-testid="save-profile-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Änderungen speichern
          </Button>
        </CardContent>
      </Card>

      {/* Password Change Card */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Key className="h-5 w-5 text-orange-400" />
            Passwort ändern
          </CardTitle>
          <CardDescription className="text-slate-400">
            Ändere dein Passwort für mehr Sicherheit
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Password */}
          <div className="space-y-2">
            <Label className="text-slate-300">Aktuelles Passwort</Label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-slate-700 border-slate-600 text-white pr-10"
                data-testid="current-password-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <Separator className="bg-slate-700" />

          {/* New Password */}
          <div className="space-y-2">
            <Label className="text-slate-300">Neues Passwort</Label>
            <div className="relative">
              <Input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mindestens 8 Zeichen"
                className="bg-slate-700 border-slate-600 text-white pr-10"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {newPassword && newPassword.length < 8 && (
              <p className="text-xs text-red-400">Passwort muss mindestens 8 Zeichen haben</p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <Label className="text-slate-300">Neues Passwort bestätigen</Label>
            <Input
              type={showNewPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Passwort wiederholen"
              className="bg-slate-700 border-slate-600 text-white"
              data-testid="confirm-password-input"
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-xs text-red-400">Passwörter stimmen nicht überein</p>
            )}
          </div>

          <Button 
            onClick={handleChangePassword}
            disabled={changingPassword || !currentPassword || !newPassword || newPassword !== confirmPassword || newPassword.length < 8}
            className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
            data-testid="change-password-btn"
          >
            {changingPassword ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Key className="h-4 w-4 mr-2" />
            )}
            Passwort ändern
          </Button>
        </CardContent>
      </Card>

      {/* Future Settings Placeholder */}
      <Card className="bg-slate-800/50 border-slate-700/50 border-dashed">
        <CardContent className="py-8 text-center">
          <p className="text-slate-500 text-sm">
            Weitere Einstellungen werden hier in Zukunft hinzugefügt...
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
